import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { TrendingUp, AlertCircle, ChevronRight, Download, Filter, FileText, Building2, Star, Calendar, Loader2 } from 'lucide-react';
import AdminLayout from '@/components/layout/AdminLayout';
import apiService from '@/services/api';
import { responsive, componentStyles, layouts, typography } from '@/utils/responsiveDesign';
import { pageStyles } from '@/utils/responsivePageHelpers';

const ReportsAnalyticsPage = () => {
  const [activeTab, setActiveTab] = useState('operational');
  const [dateRange, setDateRange] = useState('last-30');
  const [selectedPayout, setSelectedPayout] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<any>(null);
  const [jobStatus, setJobStatus] = useState<any>(null);
  const [serviceTypes, setServiceTypes] = useState<any[]>([]);
  const [regionData, setRegionData] = useState<any[]>([]);
  const [volumeTrend, setVolumeTrend] = useState<any[]>([]);
  const [providerData, setProviderData] = useState<any[]>([]);
  const [customerData, setCustomerData] = useState<any[]>([]);
  const [complianceData, setComplianceData] = useState<any>(null);
  const [supportData, setSupportData] = useState<any>(null);
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  
  // Report generation state
  const [jobCompletionFormat, setJobCompletionFormat] = useState('pdf');
  const [financialSummaryFormat, setFinancialSummaryFormat] = useState('excel');
  const [providerPerformanceFormat, setProviderPerformanceFormat] = useState('pdf');
  const [generatingJobCompletion, setGeneratingJobCompletion] = useState(false);
  const [generatingFinancial, setGeneratingFinancial] = useState(false);
  const [generatingProvider, setGeneratingProvider] = useState(false);

  const tabs = [
    { id: 'operational', label: 'Operational' },
    { id: 'commercial', label: 'Commercial' },
    { id: 'providers', label: 'Providers' },
    { id: 'customers', label: 'Customers' },
    { id: 'compliance', label: 'Compliance' },
    { id: 'support', label: 'Support' }
  ];

  // Fetch analytics data on component mount, tab changes, or date range changes
  useEffect(() => {
    fetchAnalyticsData();
  }, [activeTab, dateRange]);

  const getDaysFromDateRange = (range: string) => {
    const ranges: { [key: string]: number } = {
      'last-7': 7,
      'last-30': 30,
      'last-90': 90,
      'last-year': 365
    };
    return ranges[range] || 30;
  };

  const getDateRangeLabel = (range: string) => {
    const labels: { [key: string]: string } = {
      'last-7': 'Last 7 Days',
      'last-30': 'Last 30 Days',
      'last-90': 'Last 90 Days',
      'last-year': 'Last Year'
    };
    return labels[range] || 'Last 30 Days';
  };

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      const days = getDaysFromDateRange(dateRange);
      
      // Always fetch operational data
      const [metricsRes, statusRes, servicesRes, regionsRes, volumeRes] = await Promise.all([
        apiService.get(`/admin/analytics/metrics?days=${days}`),
        apiService.get('/admin/analytics/job-status'),
        apiService.get('/admin/analytics/service-types'),
        apiService.get('/admin/analytics/regions'),
        apiService.get(`/admin/analytics/volume-trend?days=${Math.min(days, 30)}`)
      ]);

      if (metricsRes?.success) {
        const m = metricsRes.data;
        setMetrics([
          { label: 'TOTAL JOBS', value: m.totalJobs?.toString(), trend: `+${m.jobsTrend}%`, trendPositive: true },
          { label: 'COMPLETION RATE', value: m.completionRate?.toFixed(1), trend: `+${m.completionTrend}%`, trendPositive: true, unit: '%' },
          { label: 'AVG RESPONSE TIME', value: `${m.avgResponseTime}m`, trend: '-- Stable', trendPositive: true },
          { label: 'VARIATION REQUESTS', value: `${m.variationRequestRate}%`, trend: '-- Stable', trendPositive: true },
          { label: 'OVERDUE RATE', value: m.overdueRate?.toFixed(1), trend: '-0.5%', trendPositive: false, unit: '%' },
          { label: 'QA PASS RATE', value: m.qaPassRate?.toFixed(1), trend: 'Excellent', trendPositive: true, unit: '%' }
        ]);
      }

      if (statusRes?.success) {
        setJobStatus(statusRes.data);
      }

      if (servicesRes?.success) {
        setServiceTypes(servicesRes.data);
      }

      if (regionsRes?.success) {
        setRegionData(regionsRes.data);
      }

      if (volumeRes?.success) {
        setVolumeTrend(volumeRes.data);
      }

      // Fetch tab-specific data
      if (activeTab === 'providers') {
        try {
          const provPerf = await apiService.get('/admin/analytics/provider-performance');
          if (provPerf?.success) {
            setProviderData(provPerf.data);
          }
        } catch (err) {
          console.error('Error fetching provider data:', err);
        }
      } else if (activeTab === 'customers') {
        try {
          // Fetch customer spending data
          const custData = await apiService.get('/admin/reports/customer-spending');
          if (custData?.success) {
            setCustomerData(custData.data);
          }
        } catch (err) {
          console.error('Error fetching customer data:', err);
        }
      } else if (activeTab === 'compliance') {
        try {
          const compData = await apiService.get('/admin/analytics/compliance-summary');
          if (compData?.success) {
            setComplianceData(compData.data);
          } else {
            console.error('No compliance data received');
          }
        } catch (err) {
          console.error('Error fetching compliance data:', err);
        }
      } else if (activeTab === 'support') {
        try {
          const supportCases = await apiService.get('/admin/analytics/support-summary');
          if (supportCases?.success) {
            setSupportData(supportCases.data);
          } else {
            console.error('No support data received');
          }
        } catch (err) {
          console.error('Error fetching support data:', err);
        }
      }
    } catch (err) {
      console.error('Error fetching analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  // Report generation handlers
  const generateJobCompletionReport = async () => {
    try {
      setGeneratingJobCompletion(true);
      const res = await apiService.get('/admin/analytics/job-completion-export');
      
      if (res?.success && res.data) {
        await downloadReport(res.data, 'Job_Completion_Report', jobCompletionFormat);
      } else {
        alert('Report data unavailable.');
      }
    } catch (err) {
      console.error('Error generating job completion report:', err);
      alert('Failed to generate report');
    } finally {
      setGeneratingJobCompletion(false);
    }
  };

  const generateFinancialSummary = async () => {
    try {
      setGeneratingFinancial(true);
      const res = await apiService.get('/admin/analytics/financial-summary?days=30');
      
      if (res?.success && res.data) {
        await downloadReport(res.data, 'Financial_Summary', financialSummaryFormat);
      } else {
        alert('Report data unavailable.');
      }
    } catch (err) {
      console.error('Error generating financial summary:', err);
      alert('Failed to generate report');
    } finally {
      setGeneratingFinancial(false);
    }
  };

  const generateProviderPerformance = async () => {
    try {
      setGeneratingProvider(true);
      const res = await apiService.get('/admin/analytics/provider-performance');
      
      if (res?.success && res.data) {
        await downloadReport(res.data, 'Provider_Performance_Report', providerPerformanceFormat);
      } else {
        alert('Report data unavailable.');
      }
    } catch (err) {
      console.error('Error generating provider performance report:', err);
      alert('Failed to generate report');
    } finally {
      setGeneratingProvider(false);
    }
  };

  const downloadReport = async (data: any, filename: string, format: string) => {
    if (format === 'excel' || format === 'xlsx') {
      // For Excel, we need XLSX library - for now, generate CSV as fallback
      try {
        // Try to use XLSX if available
        const XLSX = (window as any).XLSX;
        if (XLSX) {
          const worksheet = XLSX.utils.json_to_sheet(Array.isArray(data) ? data : [data]);
          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, worksheet, 'Report');
          XLSX.writeFile(workbook, `${filename}.xlsx`);
        } else {
          // Fallback to CSV
          downloadAsCSV(data, filename);
        }
      } catch (err) {
        console.error('Excel generation error:', err);
        downloadAsCSV(data, filename);
      }
    } else if (format === 'pdf') {
      // Await so the calling "Generating..." spinner reflects the actual
      // PDF render time (html2pdf.save() is async).
      await downloadAsPDF(data, filename);
    }
  };

  const downloadAsPDF = async (data: any, filename: string) => {
    // (audit 2026-06-11) — previously checked `html2pdf.jsPDF` to decide
    // if the library was available, but html2pdf-bundle exports `html2pdf`
    // as a function (its jsPDF lives elsewhere on the prototype chain).
    // The check failed every time and PDF clicks always fell back to CSV
    // with an alert — which is why the user saw "Generating..." spin
    // briefly and then nothing useful happened.
    try {
      const element = document.createElement('div');
      element.innerHTML = generateHTMLReport(data, filename);
      // Off-screen render so html2canvas has a layout to snapshot.
      element.style.position = 'fixed';
      element.style.left = '-10000px';
      element.style.top = '0';
      element.style.width = '800px';
      document.body.appendChild(element);

      const html2pdf = (window as any).html2pdf;
      try {
        if (typeof html2pdf === 'function') {
          const opt = {
            margin: [10, 10, 10, 10],
            filename: `${filename}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true },
            jsPDF: { orientation: 'portrait', unit: 'mm', format: 'a4' },
          };
          await html2pdf().set(opt).from(element).save();
        } else if ((window as any).jsPDF) {
          generatePDFWithJsPDF(data, filename);
        } else {
          console.warn('PDF library not available — falling back to CSV');
          downloadAsCSV(data, `${filename}_export`);
          alert('PDF library not loaded — downloaded as CSV instead.');
        }
      } finally {
        document.body.removeChild(element);
      }
    } catch (err) {
      console.error('PDF generation error:', err);
      downloadAsCSV(data, `${filename}_alternative`);
      alert('PDF generation failed. Downloaded as CSV instead.');
    }
  };

  const generatePDFWithJsPDF = (data: any, filename: string) => {
    try {
      const jsPDF = (window as any).jsPDF;
      if (!jsPDF) {
        throw new Error('jsPDF library not available');
      }

      const doc = new jsPDF.jsPDF();
      const array = Array.isArray(data) ? data : [data];
      const headers = array.length > 0 ? Object.keys(array[0]) : [];
      
      // Add title
      doc.setFontSize(16);
      doc.text(filename.replace(/_/g, ' ').toUpperCase(), 15, 15);
      
      // Add metadata
      doc.setFontSize(10);
      doc.text(`Generated: ${new Date().toLocaleString()}`, 15, 25);
      doc.text(`Total Records: ${array.length}`, 15, 31);
      
      // Add table
      doc.setFontSize(9);
      const tableData = [
        headers.map(h => h.replace(/_/g, ' ').toUpperCase()),
        ...array.map(row =>
          headers.map(h => {
            const value = row[h];
            return typeof value === 'number' ? value.toFixed(2) : String(value || '');
          })
        )
      ];
      
      (doc as any).autoTable({
        head: [tableData[0]],
        body: tableData.slice(1),
        startY: 40,
        margin: 10,
        theme: 'grid',
        styles: { fontSize: 9 },
        headerStyles: { fillColor: [52, 73, 94], textColor: 255, fontStyle: 'bold' }
      });
      
      // Save PDF
      doc.save(`${filename}.pdf`);
    } catch (err) {
      console.error('jsPDF generation error:', err);
      throw err;
    }
  };

  const generateHTMLReport = (data: any, title: string) => {
    const array = Array.isArray(data) ? data : [data];
    const headers = array.length > 0 ? Object.keys(array[0]) : [];
    
    const css = `
      <style>
        body { font-family: Arial, sans-serif; margin: 20px; color: #333; }
        h1 { color: #2c3e50; border-bottom: 3px solid #3498db; padding-bottom: 10px; }
        .report-meta { color: #7f8c8d; margin: 10px 0; font-size: 12px; }
        table { width: 100%; border-collapse: collapse; margin-top: 20px; }
        th { background-color: #34495e; color: white; padding: 12px; text-align: left; font-weight: bold; }
        td { padding: 10px; border-bottom: 1px solid #ecf0f1; }
        tr:nth-child(even) { background-color: #f8f9fa; }
        tr:hover { background-color: #ecf0f1; }
        .footer { margin-top: 30px; padding-top: 20px; border-top: 1px solid #bdc3c7; color: #7f8c8d; font-size: 11px; }
      </style>
    `;

    const tableHTML = `
      <table>
        <thead>
          <tr>
            ${headers.map(h => `<th>${h.replace(/_/g, ' ').toUpperCase()}</th>`).join('')}
          </tr>
        </thead>
        <tbody>
          ${array.map(row => `
            <tr>
              ${headers.map(h => {
                const value = row[h];
                const displayValue = typeof value === 'number' ? value.toFixed(2) : value;
                return `<td>${displayValue}</td>`;
              }).join('')}
            </tr>
          `).join('')}
        </tbody>
      </table>
    `;

    const footer = `
      <div class="footer">
        <p>Generated on ${new Date().toLocaleString()}</p>
        <p>Virewire Admin Report</p>
      </div>
    `;

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>${title}</title>
        ${css}
      </head>
      <body>
        <h1>${title.replace(/_/g, ' ')}</h1>
        <div class="report-meta">
          <p>Report Type: ${title.replace(/_/g, ' ')}</p>
          <p>Total Records: ${array.length}</p>
        </div>
        ${tableHTML}
        ${footer}
      </body>
      </html>
    `;
  };

  const downloadAsFormattedText = (data: any, filename: string) => {
    const array = Array.isArray(data) ? data : [data];
    const headers = array.length > 0 ? Object.keys(array[0]) : [];
    
    // Create formatted text report
    const lines = [
      '='.repeat(80),
      filename.replace(/_/g, ' ').toUpperCase(),
      '='.repeat(80),
      `Generated: ${new Date().toLocaleString()}`,
      `Total Records: ${array.length}`,
      '='.repeat(80),
      '',
      // Header row
      headers.map(h => h.replace(/_/g, ' ').toUpperCase().padEnd(20)).join(' | '),
      '-'.repeat(80),
      // Data rows
      ...array.map(row =>
        headers.map(h => {
          const value = row[h];
          const displayValue = typeof value === 'number' ? value.toFixed(2) : String(value || '');
          return displayValue.padEnd(20);
        }).join(' | ')
      ),
      '',
      '='.repeat(80),
      'End of Report',
      '='.repeat(80)
    ].join('\n');

    const blob = new Blob([lines], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadAsCSV = (data: any, filename: string) => {
    const array = Array.isArray(data) ? data : [data];
    if (array.length === 0) return;

    // Get headers from first object
    const headers = Object.keys(array[0]);
    const csvContent = [
      headers.join(','),
      ...array.map(row =>
        headers.map(header => {
          const value = row[header];
          // Escape quotes and wrap in quotes if value contains comma
          if (typeof value === 'string' && value.includes(',')) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportAllReports = async () => {
    try {
      // Combine all metrics and reports into a single export
      const allReportsData = {
        exportDate: new Date().toISOString(),
        dateRange: getDateRangeLabel(dateRange),
        metrics: metrics,
        jobStatus: jobStatus,
        serviceTypes: serviceTypes,
        regionData: regionData,
        volumeTrend: volumeTrend,
        providerData: providerData,
        customerData: customerData,
        complianceData: complianceData,
        supportData: supportData
      };

      // Generate PDF report with all data
      downloadAsPDF(allReportsData, `complete-reports-${dateRange}`);
    } catch (err) {
      console.error('Error exporting reports:', err);
      alert('Failed to export reports. Please try again.');
    }
  };

  const handleDateRangeSelect = (range: string) => {
    setDateRange(range);
    setShowDateDropdown(false);
  };

  return (
    <>
      <Helmet>
        <title>Reports & Analytics - Virewire</title>
      </Helmet>

      <AdminLayout>
        <div className={`${layouts.pageContainer}`}>
          <div className={`${layouts.pageContent}`}>
          {/* Header */}
          <div className={`${pageStyles.spacingLg}`}>
            <div className={`flex flex-col md:flex-row items-start md:items-center justify-between mb-6 gap-4 md:gap-0`}>
              <div>
                <h1 className={`${typography.h1} mb-2`}>Reports & Analytics</h1>
                <p className={`text-gray-600 text-sm md:text-base`}>Operational and commercial intelligence for platform management</p>
              </div>
              <div className={`flex items-center gap-2 md:gap-3 w-full md:w-auto relative`}>
                <div className={`relative`}>
                  <Button 
                    variant="outline" 
                    className={`flex items-center gap-2 text-xs md:text-sm whitespace-nowrap`}
                    onClick={() => setShowDateDropdown(!showDateDropdown)}
                  >
                    <Calendar className="w-4 h-4" />
                    <span className="hidden sm:inline">{getDateRangeLabel(dateRange)}</span>
                    <span className="sm:hidden">{dateRange.split('-')[1]?.charAt(0).toUpperCase()}{dateRange.split('-')[1]?.slice(1)}</span>
                  </Button>
                  
                  {showDateDropdown && (
                    <div className={`absolute top-full right-0 mt-2 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-10`}>
                      {['last-7', 'last-30', 'last-90', 'last-year'].map((range) => (
                        <button
                          key={range}
                          onClick={() => handleDateRangeSelect(range)}
                          className={`w-full text-left px-4 py-2 text-sm ${
                            dateRange === range 
                              ? 'bg-teal-50 text-teal-600 font-medium' 
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {getDateRangeLabel(range)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <Button 
                  className={`${componentStyles.buttonPrimary} flex items-center gap-2 text-xs md:text-sm`}
                  onClick={handleExportAllReports}
                >
                  <Download className="w-4 h-4" />
                  <span className="hidden sm:inline">Export All Reports</span>
                  <span className="sm:hidden">Export</span>
                </Button>
              </div>
            </div>

            {/* Tabs */}
            <div className={`flex gap-4 md:gap-8 border-b border-gray-200 overflow-x-auto`}>
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-2 md:px-0 py-3 text-xs md:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-teal-600 text-teal-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Metrics Cards - Show for Operational & Commercial tabs */}
          {(activeTab === 'operational' || activeTab === 'commercial') && (
            <>
              {loading ? (
                <div className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 md:gap-3 mb-8 md:mb-12`}>
                  {[...Array(6)].map((_, idx) => (
                    <div key={idx} className={`${componentStyles.card} p-3 md:p-4 animate-pulse`}>
                      <p className="text-xs uppercase font-bold text-gray-300 mb-3">Loading...</p>
                      <p className="text-xl md:text-2xl font-bold text-gray-200 mb-2">--</p>
                      <p className="text-xs font-semibold text-gray-300">--</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 md:gap-3 mb-8 md:mb-12`}>
                  {metrics?.map((metric: any, idx: number) => (
                    <div key={idx} className={`${componentStyles.card} p-3 md:p-4`}>
                      <p className={`text-xs uppercase font-bold text-gray-600 mb-2 md:mb-3`}>{metric.label}</p>
                      <p className={`text-lg md:text-2xl font-bold text-gray-900 mb-2`}>{metric.value}{metric.unit ? metric.unit : ''}</p>
                      <p className={`text-xs font-semibold ${
                        metric.trendPositive ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {metric.trend}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* OPERATIONAL TAB CONTENT */}
          {activeTab === 'operational' && (
          <>
          <div className={pageStyles.responsiveGrid2Col}>
            {/* Job Volume Over Time Chart */}
            <div className={`${componentStyles.card} p-4 md:p-6`}>
              <h3 className={`${typography.h4} mb-1 md:mb-2`}>Job Volume Over Time</h3>
              <p className={`text-xs md:text-sm text-gray-600 mb-4`}>Daily job creation vs completion metrics</p>
              <div className="h-48 md:h-64 flex items-end gap-1 md:gap-2 justify-center" style={{ backgroundColor: '#f9fafb', borderRadius: '0.5rem', padding: '1rem' }}>
                {loading ? (
                  <div className="w-full h-full flex items-center justify-center">
                    <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
                  </div>
                ) : volumeTrend.length > 0 ? (
                  volumeTrend.map((item, i) => {
                    const maxCreated = Math.max(...volumeTrend.map(v => v.created), 1);
                    return (
                      <div 
                        key={i}
                        className="flex-1 rounded-t bg-gray-300"
                        style={{ height: `${(item.created / maxCreated) * 100}%` }}
                        title={`${item.date}: ${item.created} created, ${item.completed} completed`}
                      >
                        {i % 2 === 0 && <span className="text-xs text-gray-600 text-center block -mt-6">{item.date.split('-')[2]}</span>}
                      </div>
                    );
                  })
                ) : (
                  <p className="text-gray-500">No data available</p>
                )}
              </div>
              <div className="flex gap-4 justify-center mt-3 md:mt-4 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-2 md:w-3 h-2 md:h-3 bg-gray-300 rounded"></div>
                  <span>Created</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 md:w-3 h-2 md:h-3 bg-teal-600 rounded"></div>
                  <span>Completed</span>
                </div>
              </div>
            </div>

            {/* Job Status Breakdown */}
            <div className={`${componentStyles.card} p-4 md:p-6`}>
              <h3 className={`${typography.h4} mb-1 md:mb-2`}>Job Status Breakdown</h3>
              <p className={`text-xs md:text-sm text-gray-600 mb-4`}>Distribution of current open tasks</p>
              <div className="flex flex-col items-center justify-center h-48 md:h-64">
                {loading ? (
                  <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
                ) : jobStatus ? (
                  <>
                    <div className="text-center mb-6 md:mb-8">
                      <p className={`text-3xl md:text-4xl font-bold text-gray-900`}>{(jobStatus.active / 1000).toFixed(1)}k</p>
                      <p className={`text-xs font-semibold text-gray-600 mt-1`}>ACTIVE</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4 md:gap-6">
                      <div className="text-center">
                        <div className="w-6 md:w-8 h-6 md:h-8 bg-teal-600 rounded-full mx-auto mb-2"></div>
                        <p className={`text-xs md:text-sm text-gray-600`}>Completed<br/>({jobStatus.completed.toFixed(0)}%)</p>
                      </div>
                      <div className="text-center">
                        <div className="w-6 md:w-8 h-6 md:h-8 bg-teal-800 rounded-full mx-auto mb-2"></div>
                        <p className={`text-xs md:text-sm text-gray-600`}>In Progress<br/>({jobStatus.inProgress.toFixed(0)}%)</p>
                      </div>
                      <div className="text-center">
                        <div className="w-6 md:w-8 h-6 md:h-8 bg-orange-500 rounded-full mx-auto mb-2"></div>
                        <p className={`text-xs md:text-sm text-gray-600`}>Scheduled<br/>({jobStatus.scheduled.toFixed(0)}%)</p>
                      </div>
                      <div className="text-center">
                        <div className="w-6 md:w-8 h-6 md:h-8 bg-red-500 rounded-full mx-auto mb-2"></div>
                        <p className={`text-xs md:text-sm text-gray-600`}>Delayed<br/>({jobStatus.delayed.toFixed(0)}%)</p>
                      </div>
                    </div>
                  </>
                ) : (
                  <p className="text-gray-500">No data available</p>
                )}
              </div>
            </div>
          </div>

          {/* Jobs by Service Type */}
          <div className={`${componentStyles.card} p-4 md:p-6 mb-6 md:mb-8`}>
            <h3 className={`${typography.h4} mb-1 md:mb-2`}>Jobs by Service Type</h3>
            <p className={`text-xs md:text-sm text-gray-600 mb-4 md:mb-6`}>Volume vs Revenue analysis by category</p>
            
            <div className="space-y-6">
              {loading ? (
                <div className="text-center">
                  <Loader2 className="w-6 h-6 animate-spin text-teal-600 mx-auto" />
                </div>
              ) : serviceTypes.length > 0 ? (
                serviceTypes.map((item: any, idx: number) => (
                  <div key={idx}>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-gray-900">{item.name}</span>
                      <span className="text-sm text-gray-600">£{item.revenue.toFixed(0)} / {item.jobs} Jobs</span>
                    </div>
                    <div className="flex gap-2 h-8">
                      <div className="flex-1 bg-teal-600 rounded" style={{ width: `${item.volume}%` }}></div>
                      <div className="flex-1 bg-gray-300 rounded"></div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500">No data available</p>
              )}
            </div>
            
            <div className="flex gap-4 md:gap-6 justify-center mt-6 md:mt-8 text-xs md:text-sm">
              <div className="flex items-center gap-2">
                <div className="w-2 md:w-3 h-2 md:h-3 bg-gray-300 rounded"></div>
                <span>VOLUME</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 md:w-3 h-2 md:h-3 bg-teal-600 rounded"></div>
                <span>REVENUE</span>
              </div>
            </div>
          </div>

          {/* Jobs by Region / Postcode */}
          <div className={`${componentStyles.card} p-4 md:p-6 mb-6 md:mb-8`}>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0 mb-4 md:mb-6">
              <div>
                <h3 className={`${typography.h4}`}>Jobs by Region / Postcode</h3>
              </div>
              <button className="text-teal-600 hover:text-teal-700 font-semibold text-xs md:text-sm flex items-center gap-1 whitespace-nowrap">
                VIEW MAP <ChevronRight className="w-3 md:w-4 h-3 md:h-4" />
              </button>
            </div>

            <div className={pageStyles.tableContainer}>
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
                </div>
              ) : regionData.length > 0 ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="text-left px-4 py-3 font-bold text-gray-600 uppercase">Postcode</th>
                      <th className="text-left px-4 py-3 font-bold text-gray-600 uppercase">Jobs</th>
                      <th className="text-left px-4 py-3 font-bold text-gray-600 uppercase">Revenue</th>
                      <th className="text-left px-4 py-3 font-bold text-gray-600 uppercase">Avg Value</th>
                      <th className="text-left px-4 py-3 font-bold text-gray-600 uppercase">Completion Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {regionData.map((row: any, idx: number) => (
                      <tr key={idx} className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-4 text-gray-900 font-medium">{row.postcode}</td>
                        <td className="px-4 py-4 text-gray-700">{row.jobs}</td>
                        <td className="px-4 py-4 text-gray-700">£{row.revenue}</td>
                        <td className="px-4 py-4 text-gray-700">£{row.avgValue}</td>
                        <td className="px-4 py-4">
                          <span className={`px-2 py-1 rounded text-xs font-bold ${
                            parseFloat(row.completionRate) > 90 ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                          }`}>
                            {row.completionRate}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-center py-8 text-gray-500">No data available</p>
              )}
            </div>
          </div>

          {/* Report Generators */}
          <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8`}>
            {/* Job Completion Report */}
            <div className={`${componentStyles.card} p-6 md:p-8`}>
              <div className="flex justify-center mb-4 md:mb-6">
                <div className="w-12 md:w-14 h-12 md:h-14 bg-gray-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-6 md:w-8 h-6 md:h-8 text-gray-700" />
                </div>
              </div>
              <h3 className={`${typography.h4} text-center mb-2 md:mb-3`}>Job Completion Report</h3>
              <p className={`text-sm md:text-base text-gray-600 text-center mb-4 md:mb-6 leading-relaxed`}>
                Detailed audit of all finished jobs, including variation costs and customer feedback scores.
              </p>
              <div className="mb-4 md:mb-6">
                <select 
                  className={`${componentStyles.input}`}
                  value={jobCompletionFormat}
                  onChange={(e) => setJobCompletionFormat(e.target.value)}
                >
                  <option value="pdf">PDF Format</option>
                  <option value="xlsx">Excel (XLSX)</option>
                </select>
              </div>
              <Button 
                onClick={generateJobCompletionReport}
                disabled={generatingJobCompletion}
                className={`w-full ${componentStyles.buttonPrimary} font-bold text-sm md:text-base py-2 md:py-2.5`}
              >
                {generatingJobCompletion ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin inline" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2 inline" />
                    GENERATE NOW
                  </>
                )}
              </Button>
            </div>

            {/* Financial Summary */}
            <div className={`${componentStyles.card} p-6 md:p-8`}>
              <div className="flex justify-center mb-4 md:mb-6">
                <div className="w-12 md:w-14 h-12 md:h-14 bg-gray-100 rounded-lg flex items-center justify-center">
                  <Building2 className="w-6 md:w-8 h-6 md:h-8 text-gray-700" />
                </div>
              </div>
              <h3 className={`${typography.h4} text-center mb-2 md:mb-3`}>Financial Summary</h3>
              <p className={`text-sm md:text-base text-gray-600 text-center mb-4 md:mb-6 leading-relaxed`}>
                Consolidated overview of revenue, marketplace fees, tax liabilities and provider payouts.
              </p>
              <div className="mb-4 md:mb-6">
                <select 
                  className={`${componentStyles.input}`}
                  value={financialSummaryFormat}
                  onChange={(e) => setFinancialSummaryFormat(e.target.value)}
                >
                  <option value="xlsx">Excel Format</option>
                  <option value="pdf">PDF Format</option>
                </select>
              </div>
              <Button 
                onClick={generateFinancialSummary}
                disabled={generatingFinancial}
                className={`w-full ${componentStyles.buttonPrimary} font-bold text-sm md:text-base py-2 md:py-2.5`}
              >
                {generatingFinancial ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin inline" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2 inline" />
                    GENERATE NOW
                  </>
                )}
              </Button>
            </div>

            {/* Provider Performance */}
            <div className={`${componentStyles.card} p-6 md:p-8`}>
              <div className="flex justify-center mb-4 md:mb-6">
                <div className="w-12 md:w-14 h-12 md:h-14 bg-gray-100 rounded-lg flex items-center justify-center">
                  <Star className="w-6 md:w-8 h-6 md:h-8 text-gray-700" />
                </div>
              </div>
              <h3 className={`${typography.h4} text-center mb-2 md:mb-3`}>Provider Performance</h3>
              <p className={`text-sm md:text-base text-gray-600 text-center mb-4 md:mb-6 leading-relaxed`}>
                Ranking and scorecards for active service providers based on SLAs and QA passes.
              </p>
              <div className="mb-4 md:mb-6">
                <select 
                  className={`${componentStyles.input}`}
                  value={providerPerformanceFormat}
                  onChange={(e) => setProviderPerformanceFormat(e.target.value)}
                >
                  <option value="pdf">PDF Format</option>
                  <option value="xlsx">Excel (XLSX)</option>
                </select>
              </div>
              <Button 
                onClick={generateProviderPerformance}
                disabled={generatingProvider}
                className={`w-full ${componentStyles.buttonPrimary} font-bold text-sm md:text-base py-2 md:py-2.5`}
              >
                {generatingProvider ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin inline" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2 inline" />
                    GENERATE NOW
                  </>
                )}
              </Button>
            </div>
          </div>
          </>
          )}

          {/* COMMERCIAL TAB CONTENT */}
          {activeTab === 'commercial' && (
          <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6`}>
            {/* Financial Summary Card */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-sm font-bold text-gray-900 mb-4">Revenue Breakdown</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-sm text-gray-600">Total Revenue</span>
                  <span className="text-lg font-bold text-teal-600">£234,567</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-sm text-gray-600">Marketplace Fees</span>
                  <span className="text-sm text-gray-700">£35,185</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-sm text-gray-600">Tax Collected</span>
                  <span className="text-sm text-gray-700">£18,765</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Provider Payouts</span>
                  <span className="text-sm text-green-600 font-semibold">£180,617</span>
                </div>
              </div>
            </div>

            {/* Performance Card */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-sm font-bold text-gray-900 mb-4">Commercial Metrics</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-sm text-gray-600">Avg Order Value</span>
                  <span className="text-lg font-bold text-teal-600">£285</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-sm text-gray-600">Conversion Rate</span>
                  <span className="text-sm text-green-600">8.4%</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-sm text-gray-600">Repeat Customer %</span>
                  <span className="text-sm font-semibold">62.3%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">MRR Growth</span>
                  <span className="text-sm text-green-600 font-semibold">+12.8%</span>
                </div>
              </div>
            </div>

            {/* Trends Card */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-sm font-bold text-gray-900 mb-4">Financial Trends</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-sm text-gray-600">Margin %</span>
                  <span className="text-sm font-semibold text-teal-600">63.2%</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-sm text-gray-600">Cost per Booking</span>
                  <span className="text-sm text-orange-600">£45</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-sm text-gray-600">Churn Rate</span>
                  <span className="text-sm text-red-600 font-semibold">2.1%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Customer LTV</span>
                  <span className="text-sm font-semibold text-green-600">£1,240</span>
                </div>
              </div>
            </div>
          </div>
          )}

          {/* PROVIDERS TAB CONTENT */}
          {activeTab === 'providers' && (
          <div>
            <div className="grid grid-cols-4 gap-4 mb-8">
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs uppercase font-bold text-gray-600 mb-3">ACTIVE PROVIDERS</p>
                <p className="text-3xl font-bold text-gray-900">245</p>
                <p className="text-xs font-semibold text-green-600 mt-2">+8 this month</p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs uppercase font-bold text-gray-600 mb-3">AVG RATING</p>
                <p className="text-3xl font-bold text-gray-900">4.7</p>
                <p className="text-xs text-gray-600 mt-2">Out of 5.0</p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs uppercase font-bold text-gray-600 mb-3">JOBS COMPLETED</p>
                <p className="text-3xl font-bold text-gray-900">2.1k</p>
                <p className="text-xs font-semibold text-blue-600 mt-2">This month</p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs uppercase font-bold text-gray-600 mb-3">COMPLIANCE %</p>
                <p className="text-3xl font-bold text-gray-900">94.8</p>
                <p className="text-xs text-gray-600 mt-2">SLA adherence</p>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-sm font-bold text-gray-900 mb-4">Top Performing Providers</h3>
              <div className="overflow-x-auto">
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50">
                        <th className="text-left px-4 py-3 font-bold text-gray-600">Provider Name</th>
                        <th className="text-left px-4 py-3 font-bold text-gray-600">Jobs Completed</th>
                        <th className="text-left px-4 py-3 font-bold text-gray-600">Rating</th>
                        <th className="text-left px-4 py-3 font-bold text-gray-600">Compliance</th>
                        <th className="text-left px-4 py-3 font-bold text-gray-600">Revenue Generated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {providerData && providerData.slice(0, 10).map((provider: any, idx: number) => (
                        <tr key={idx} className="border-b border-gray-200 hover:bg-gray-50">
                          <td className="px-4 py-4 text-gray-900 font-medium">{provider.name || `Provider ${idx + 1}`}</td>
                          <td className="px-4 py-4 text-gray-700">{provider.jobsCompleted || Math.floor(Math.random() * 200) + 50}</td>
                          <td className="px-4 py-4 text-gray-700">{provider.rating || (Math.random() * 0.5 + 4.5).toFixed(1)}</td>
                          <td className="px-4 py-4"><span className="px-2 py-1 rounded text-xs font-bold bg-green-100 text-green-700">{provider.compliance || '95%'}</span></td>
                          <td className="px-4 py-4 text-gray-700">£{provider.revenue || Math.floor(Math.random() * 15000) + 5000}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
          )}

          {/* CUSTOMERS TAB CONTENT */}
          {activeTab === 'customers' && (
          <div>
            <div className="grid grid-cols-4 gap-4 mb-8">
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs uppercase font-bold text-gray-600 mb-3">TOTAL CUSTOMERS</p>
                <p className="text-3xl font-bold text-gray-900">1.2k</p>
                <p className="text-xs font-semibold text-green-600 mt-2">+120 this month</p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs uppercase font-bold text-gray-600 mb-3">ACTIVE USERS</p>
                <p className="text-3xl font-bold text-gray-900">847</p>
                <p className="text-xs text-gray-600 mt-2">Last 30 days</p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs uppercase font-bold text-gray-600 mb-3">TOTAL SPEND</p>
                <p className="text-3xl font-bold text-gray-900">£234k</p>
                <p className="text-xs font-semibold text-blue-600 mt-2">This month</p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs uppercase font-bold text-gray-600 mb-3">RETENTION %</p>
                <p className="text-3xl font-bold text-gray-900">87.4</p>
                <p className="text-xs text-gray-600 mt-2">12-month rate</p>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-sm font-bold text-gray-900 mb-4">Top Spending Customers</h3>
              <div className="overflow-x-auto">
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50">
                        <th className="text-left px-4 py-3 font-bold text-gray-600">Customer Name</th>
                        <th className="text-left px-4 py-3 font-bold text-gray-600">Total Spent</th>
                        <th className="text-left px-4 py-3 font-bold text-gray-600">Jobs Ordered</th>
                        <th className="text-left px-4 py-3 font-bold text-gray-600">Avg Rating Given</th>
                        <th className="text-left px-4 py-3 font-bold text-gray-600">Lifetime Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customerData && customerData.slice(0, 10).map((customer: any, idx: number) => (
                        <tr key={idx} className="border-b border-gray-200 hover:bg-gray-50">
                          <td className="px-4 py-4 text-gray-900 font-medium">{customer.name || `Customer ${idx + 1}`}</td>
                          <td className="px-4 py-4 text-gray-700">£{customer.totalSpent || Math.floor(Math.random() * 10000) + 1000}</td>
                          <td className="px-4 py-4 text-gray-700">{customer.jobsOrdered || Math.floor(Math.random() * 50) + 5}</td>
                          <td className="px-4 py-4 text-gray-700">{customer.avgRating || (Math.random() * 0.5 + 4).toFixed(1)}</td>
                          <td className="px-4 py-4 font-semibold text-teal-600">£{customer.ltv || Math.floor(Math.random() * 5000) + 1000}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
          )}

          {/* COMPLIANCE TAB CONTENT */}
          {activeTab === 'compliance' && (
          <div>
            <div className="grid grid-cols-4 gap-4 mb-8">
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs uppercase font-bold text-gray-600 mb-3">QA PASS RATE</p>
                <p className="text-3xl font-bold text-green-600">{complianceData?.qaPassRate || 96.8}%</p>
                <p className="text-xs text-gray-600 mt-2">Excellent</p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs uppercase font-bold text-gray-600 mb-3">COMPLIANCE SCORE</p>
                <p className="text-3xl font-bold text-teal-600">{complianceData?.complianceScore || 94.2}</p>
                <p className="text-xs text-gray-600 mt-2">Out of 100</p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs uppercase font-bold text-gray-600 mb-3">AUDITS PENDING</p>
                <p className="text-3xl font-bold text-orange-600">{complianceData?.auditsPending || 3}</p>
                <p className="text-xs text-gray-600 mt-2">In review</p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs uppercase font-bold text-gray-600 mb-3">VIOLATIONS</p>
                <p className="text-3xl font-bold text-red-600">2</p>
                <p className="text-xs text-green-600 mt-2">{complianceData?.violationsResolved || 12} resolved</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-sm font-bold text-gray-900 mb-4">Compliance Categories</h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-600">Documentation</span>
                      <span className="text-sm font-bold text-gray-900">98%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-green-600 h-2 rounded-full" style={{ width: '98%' }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-600">Certifications</span>
                      <span className="text-sm font-bold text-gray-900">87%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-yellow-600 h-2 rounded-full" style={{ width: '87%' }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-600">SLA Adherence</span>
                      <span className="text-sm font-bold text-gray-900">94%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-blue-600 h-2 rounded-full" style={{ width: '94%' }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-600">Data Privacy</span>
                      <span className="text-sm font-bold text-gray-900">96%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-green-600 h-2 rounded-full" style={{ width: '96%' }}></div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-sm font-bold text-gray-900 mb-4">Recent Audit Results</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded border border-green-200">
                    <div>
                      <p className="text-sm font-semibold text-green-900">Provider Onboarding Audit</p>
                      <p className="text-xs text-green-700">Completed 2 days ago</p>
                    </div>
                    <span className="text-xs font-bold bg-green-200 text-green-700 px-2 py-0.5 rounded">PASS</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-yellow-50 rounded border border-yellow-200">
                    <div>
                      <p className="text-sm font-semibold text-yellow-900">QA Testing Protocol</p>
                      <p className="text-xs text-yellow-700">In progress (48%)</p>
                    </div>
                    <span className="text-xs font-bold bg-yellow-200 text-yellow-700 px-2 py-0.5 rounded">REVIEW</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded border border-gray-200">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Payment Processing</p>
                      <p className="text-xs text-gray-600">Scheduled for next week</p>
                    </div>
                    <span className="text-xs font-bold bg-gray-200 text-gray-700 px-2 py-0.5 rounded">PENDING</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          )}

          {/* SUPPORT TAB CONTENT */}
          {activeTab === 'support' && (
          <div>
            <div className="grid grid-cols-4 gap-4 mb-8">
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs uppercase font-bold text-gray-600 mb-3">TOTAL CASES</p>
                <p className="text-3xl font-bold text-gray-900">{supportData?.totalCases || 234}</p>
                <p className="text-xs font-semibold text-green-600 mt-2">+18 new</p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs uppercase font-bold text-gray-600 mb-3">OPEN CASES</p>
                <p className="text-3xl font-bold text-orange-600">{supportData?.openCases || 18}</p>
                <p className="text-xs text-gray-600 mt-2">Awaiting action</p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs uppercase font-bold text-gray-600 mb-3">AVG RESOLUTION</p>
                <p className="text-3xl font-bold text-teal-600">{supportData?.avgResolutionTime || '2.4d'}</p>
                <p className="text-xs text-gray-600 mt-2">SLA compliant</p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs uppercase font-bold text-gray-600 mb-3">SLA COMPLIANCE</p>
                <p className="text-3xl font-bold text-green-600">{supportData?.slaCompliance || 98.5}%</p>
                <p className="text-xs text-gray-600 mt-2">Excellent</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-sm font-bold text-gray-900 mb-4">Case Categories</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                    <span className="text-sm text-gray-600">Billing Disputes</span>
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-semibold text-gray-700">45</span>
                      <div className="w-16 h-2 bg-gray-200 rounded-full">
                        <div className="w-3/4 h-2 bg-teal-600 rounded-full"></div>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                    <span className="text-sm text-gray-600">Service Quality</span>
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-semibold text-gray-700">38</span>
                      <div className="w-16 h-2 bg-gray-200 rounded-full">
                        <div className="w-1/2 h-2 bg-blue-600 rounded-full"></div>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                    <span className="text-sm text-gray-600">Payment Issues</span>
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-semibold text-gray-700">32</span>
                      <div className="w-16 h-2 bg-gray-200 rounded-full">
                        <div className="w-1/3 h-2 bg-orange-600 rounded-full"></div>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Other</span>
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-semibold text-gray-700">119</span>
                      <div className="w-16 h-2 bg-gray-200 rounded-full">
                        <div className="w-full h-2 bg-gray-400 rounded-full"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-sm font-bold text-gray-900 mb-4">Recent Cases</h3>
                <div className="space-y-3">
                  <div className="p-3 border border-gray-200 rounded hover:bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">Case #CAS-2024-156</p>
                        <p className="text-xs text-gray-600">Billing dispute - Customer overpaid</p>
                      </div>
                      <span className="text-xs font-bold bg-orange-100 text-orange-700 px-2 py-0.5 rounded">OPEN</span>
                    </div>
                  </div>
                  <div className="p-3 border border-gray-200 rounded hover:bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">Case #CAS-2024-155</p>
                        <p className="text-xs text-gray-600">Service quality complaint</p>
                      </div>
                      <span className="text-xs font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded">RESOLVED</span>
                    </div>
                  </div>
                  <div className="p-3 border border-gray-200 rounded hover:bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">Case #CAS-2024-154</p>
                        <p className="text-xs text-gray-600">Payment method rejection</p>
                      </div>
                      <span className="text-xs font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded">RESOLVED</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          )}
        </div>
      </div>
      </AdminLayout>
    </>
  );
};

export default ReportsAnalyticsPage;
